import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { FlexContainer } from '@/components/containers/FlexContainer/FlexContainer';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';

const googleApiKey = 'AIzaSyC75q_cO4SIMGhQgRJwAIpNfNQ5cXwVXnU';

export const GMapsMultipleMarkerComponent = (props) => {
  const {
    center,
    markers,
    customHeight,
    onClickMarkers,
    setMapKeyCounter,
    mapKeyCounter,
    smallPins,
    setSmallPins,
    isDesktop,
  } = props;

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: googleApiKey,
  });

  const [map, setMap] = useState(null);
  const [showFullScreen, setShowFullScreen] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(12);
  const [selectedMarkerId, setSelectedMarkerId] = useState(null);

  const [currentCenter, setCurrentCenter] = useState({
    lat: parseFloat(center?.space_latitude?.trim() || 0),
    lng: parseFloat(center?.space_logitude?.trim() || 0),
  });

  useEffect(() => {
    if (center?.space_latitude && center?.space_logitude) {
      setCurrentCenter({
        lat: parseFloat(center.space_latitude.trim()),
        lng: parseFloat(center.space_logitude.trim()),
      });
    }
  }, [center]);

  const onLoad = useCallback((map) => setMap(map), []);
  const onUnmount = useCallback(() => setMap(null), []);

  const handleDragEnd = () => {
    if (map) {
      const newCenter = map.getCenter().toJSON();
      setCurrentCenter(newCenter);
      setSmallPins(false);
    }
  };

  const selectMarker = (id, coordinates) => {
    setSelectedMarkerId(id);
    onClickMarkers(id, coordinates);
    setMapKeyCounter((prev) => prev + 1);
    setSmallPins(false);
  };

  const handleMapClick = () => {
    setSmallPins(false);
    setZoomLevel((prevZoom) => Math.min(prevZoom + 1, 20));
  };

  const processedMarkers = useMemo(() => {
    if (!markers || markers.length === 0) return [];

    const orbitRadius = 0.0006;
    const positionGroups = {};

    const validMarkers = markers
      .map((m) => ({
        ...m,
        lat: parseFloat(m.space_latitude?.trim()),
        lng: parseFloat(m.space_logitude?.trim()),
      }))
      .filter((m) => !isNaN(m.lat) && !isNaN(m.lng));

    validMarkers.forEach((m) => {
      const key = `${m.lat}_${m.lng}`;
      if (!positionGroups[key]) positionGroups[key] = [];
      positionGroups[key].push(m);
    });

    return Object.values(positionGroups).flatMap((group) => {
      if (group.length === 1) {
        return [{ ...group[0], displayLat: group[0].lat, displayLng: group[0].lng }];
      }

      return group.map((marker, index) => {
        const angle = (index * 2 * Math.PI) / group.length;
        return {
          ...marker,
          displayLat: marker.lat + orbitRadius * Math.cos(angle),
          displayLng: marker.lng + orbitRadius * Math.sin(angle),
        };
      });
    });
  }, [markers]);

  const mapStyles = [
    { featureType: 'all', elementType: 'geometry', stylers: [{ color: '#f5f5f5' }] },
    { featureType: 'all', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
    { featureType: 'all', elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
    { featureType: 'all', elementType: 'labels.text.stroke', stylers: [{ color: '#f5f5f5' }] },
    { featureType: 'landscape', elementType: 'geometry', stylers: [{ color: '#f5f5f5' }] },
    { featureType: 'landscape.natural', elementType: 'geometry', stylers: [{ color: '#eef2ee' }] },
    { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#C9E8C9' }] },
    { featureType: 'poi.park', elementType: 'labels.text.fill', stylers: [{ color: '#9e9e9e' }] },
    { featureType: 'poi', elementType: 'geometry', stylers: [{ color: '#eeeeee' }] },
    { featureType: 'poi', elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
    { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#D9E3F0' }] },
    { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#9e9e9e' }] },
    { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
    { featureType: 'road.arterial', elementType: 'geometry.fill', stylers: [{ color: '#E0E7ED' }] },
    {
      featureType: 'road.arterial',
      elementType: 'labels.text.fill',
      stylers: [{ color: '#757575' }],
    },
    { featureType: 'road.highway', elementType: 'geometry.fill', stylers: [{ color: '#D6E1EA' }] },
    {
      featureType: 'road.highway',
      elementType: 'labels.text.fill',
      stylers: [{ color: '#616161' }],
    },
    { featureType: 'transit.line', elementType: 'geometry', stylers: [{ color: '#e5e5e5' }] },
  ];

  const containerStyle = {
    width: '100%',
    height: customHeight ?? '500px',
    borderRadius: '8px',
  };

  const fullScreenStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    zIndex: 9999,
  };

  const isReadyToMap = isLoaded && markers;

  return isReadyToMap ? (
    <>
      <FlexContainer className="flex-column m-margtb20 border-rad8px grayShadowBottom width100">
        <GoogleMap
          key={mapKeyCounter}
          mapContainerStyle={containerStyle}
          center={currentCenter}
          onClick={handleMapClick}
          zoom={zoomLevel}
          onLoad={onLoad}
          onUnmount={onUnmount}
          onDragEnd={handleDragEnd}
          options={{
            styles: mapStyles,
            disableDefaultUI: true,
            draggable: true,
            scrollwheel: true,
            gestureHandling: 'greedy',
          }}
        >
          {window.google &&
            processedMarkers.map((space, index) => {
              const isSelected = space.id_relationship === selectedMarkerId;
              const price = space.formattedPrice || '$0';

              let selectedSvg = ``;
              let normalSvg = ``;
              let smallSvg = ``;

              if (isDesktop) {
                selectedSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="110" height="38" viewBox="0 0 110 38"><path d="M5,18 Q5,5 35,5 H75 Q105,5 105,18 V25 Q105,33 60,33 L55,38 L50,33 Q5,33 5,25 Z" fill="#e465d2"/><text x="55" y="23" text-anchor="middle" font-family="Arial" font-size="14" fill="white" font-weight="bold">${price}</text></svg>`;
                normalSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="110" height="38" viewBox="0 0 110 38"><path d="M5,18 Q5,5 35,5 H75 Q105,5 105,18 V25 Q105,33 60,33 L55,38 L50,33 Q5,33 5,25 Z" fill="#2C3E50"/><text x="55" y="23" text-anchor="middle" font-family="Arial" font-size="14" fill="white" font-weight="bold">${price}</text></svg>`;
                smallSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="36" height="12" viewBox="0 0 36 12"><rect x="0" y="0" width="36" height="12" rx="6" ry="6" fill="#2C3E50"/></svg>`;
              } else {
                selectedSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="100" height="50"><path d="M5,20 Q5,5 30,5 H50 Q75,5 75,20 V30 Q75,45 40,45 L35,55 L30,45 Q5,45 5,30 Z" fill="#e465d2"/><text x="40" y="30" text-anchor="middle" font-family="Arial" font-size="16" fill="white" font-weight="bold">${price}</text></svg>`;
                normalSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="100" height="50"><path d="M5,20 Q5,5 30,5 H50 Q75,5 75,20 V30 Q75,45 40,45 L35,55 L30,45 Q5,45 5,30 Z" fill="#2C3E50"/><text x="40" y="30" text-anchor="middle" font-family="Arial" font-size="16" fill="white" font-weight="bold">${price}</text></svg>`;
                smallSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="30" height="15" viewBox="0 0 30 15"><rect x="0" y="0" width="30" height="15" rx="5" ry="5" fill="#2C3E50"/></svg>`;
              }

              const regularSvg = isSelected ? selectedSvg : normalSvg;
              const encoded =
                'data:image/svg+xml;charset=UTF-8,' +
                encodeURIComponent(smallPins ? smallSvg : regularSvg);

              return (
                <Marker
                  key={`${space.id_relationship}-${index}`}
                  position={{ lat: space.displayLat, lng: space.displayLng }}
                  zIndex={isSelected ? 999 : index}
                  onClick={() =>
                    selectMarker(space.id_relationship, {
                      lat: space.lat,
                      lng: space.lng,
                    })
                  }
                  icon={{
                    url: encoded,
                    scaledSize: smallPins
                      ? new window.google.maps.Size(30, 15)
                      : isSelected
                      ? new window.google.maps.Size(84, 70)
                      : new window.google.maps.Size(60, 50),
                    anchor: smallPins
                      ? new window.google.maps.Point(15, 15)
                      : isSelected
                      ? new window.google.maps.Point(42, 70)
                      : new window.google.maps.Point(30, 50),
                  }}
                />
              );
            })}
        </GoogleMap>
      </FlexContainer>

      {showFullScreen && (
        <div style={fullScreenStyle} onClick={() => setShowFullScreen(false)}>
          <GoogleMap
            mapContainerStyle={{ width: '100%', height: '100%' }}
            center={currentCenter}
            zoom={16}
            onLoad={onLoad}
            onUnmount={onUnmount}
            options={{
              styles: mapStyles,
              disableDefaultUI: false,
              gestureHandling: 'auto',
            }}
          />
        </div>
      )}
    </>
  ) : null;
};
